<template>
  <div>
    <v-select
      v-model="selectedRating"
      :items="riskTypes"
      item-text="name"
      item-value="id"
      :menu-props="{ maxHeight: '400' }"
      :label="label"
      :placeholder="placeholder"
      return-object
      :clearable="clearable"
      class="rating-list"
      @change="handleChangeRating"
      @click:clear="handleClickClearRating"
    >
      <template v-slot:selection="{ item }">
        <v-chip v-if="chips && item" small>{{ item.display }}</v-chip>
        <div v-if="!chips && item" class="v-select__slot">
          <div class="v-select__selections">
            <div class="v-select__selection v-select__selection--comma">
              {{ item.display }}
            </div>
          </div>
        </div>
      </template>
      <template v-slot:item="{ active, item, attrs, on }">
        <v-list-item v-on="on" v-bind="attrs">
          <v-list-item-content>
            <v-list-item-title>
              <v-row no-gutters align="center">
                <span :class="{ 'ml-5': item.parentId }">{{
                  item.display
                }}</span>
              </v-row>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-select>
  </div>
</template>

<script>
import lodashGet from "lodash/get";
import lodashOrderBy from "lodash/orderBy";
import lodashFind from "lodash/find";
import lodashRemove from "lodash/remove";

import { getRiskCategories } from "@/services/controlRisks/risks.service";
import { hasPlusCyberAccess } from "@/services/user/user.service";
import {
  RISK_RATINGS_AVAILABLE,
  RISK_RATING_PRIMARIES,
} from "@/common/constants/controlRisks/risks";

const EMITTED_ACTIONS = {
  CHANGE: "change",
  DELETE: "delete",
  CLEAR: "clear",
};

export default {
  name: "RiskRatingPicker",
  components: {},
  props: {
    params: {
      type: Object,
      default: () => {
        return {};
      },
    },
    givenRating: {
      type: Object,
    },
    label: {
      type: String,
    },
    placeholder: {
      type: String,
      default: "",
    },
    chips: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    hideCyberSubs: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const availableRiskTypes = [];
    const availableSubRiskTypes = [];
    RISK_RATINGS_AVAILABLE.forEach((r) => {
      if (r.parentId) {
        availableSubRiskTypes.push(r);
      } else {
        availableRiskTypes.push(r);
      }
    });

    if (this.hideCyberSubs) {
      lodashRemove(availableSubRiskTypes, (r) => {
        return r.parentId === RISK_RATING_PRIMARIES.cyber;
      });
    }

    if (!hasPlusCyberAccess()) {
      lodashRemove(availableRiskTypes, (r) => {
        return r.id === RISK_RATING_PRIMARIES.cyber;
      });
      lodashRemove(availableSubRiskTypes, (r) => {
        return r.parentId === RISK_RATING_PRIMARIES.cyber;
      });
    }

    if (lodashGet(this.params, "riskParentCategories")) {
      const riskParentCategories = lodashGet(
        this.params,
        "riskParentCategories"
      ).split(",");
      lodashRemove(availableRiskTypes, (r) => {
        return !riskParentCategories.includes(r.id);
      });
    }

    if (lodashGet(this.params, "riskSubCategories")) {
      const riskSubCategories = lodashGet(
        this.params,
        "riskSubCategories"
      ).split(",");
      lodashRemove(availableSubRiskTypes, (r) => {
        return !riskSubCategories.includes(r.id);
      });
    }

    return {
      availableRiskTypes: availableRiskTypes,
      availableSubRiskTypes: availableSubRiskTypes,
      riskTypes: null,
      selectedRating: null,
    };
  },
  async mounted() {
    await this.getFilteredRiskTypes();
  },
  methods: {
    async getFilteredRiskTypes() {
      let rtArr = [];
      let riskTypes = await getRiskCategories();
      riskTypes = lodashOrderBy(riskTypes, ["name"], ["asc"]);

      let primaryRTs = [];
      riskTypes.forEach((r) => {
        const riskType = lodashFind(this.availableRiskTypes, ["id", r.id]);
        if (riskType) {
          primaryRTs.push(r);
        }
      });
      primaryRTs = lodashOrderBy(primaryRTs, ["order"], ["asc"]);

      let subRTs = [];
      riskTypes.forEach((r) => {
        const riskType = lodashFind(this.availableSubRiskTypes, ["id", r.id]);
        if (riskType) {
          subRTs.push(r);
        }
      });
      subRTs = lodashOrderBy(subRTs, ["name"], ["asc"]);

      primaryRTs.forEach((rt) => {
        rtArr.push(rt);

        riskTypes.forEach((riskType) => {
          if (
            riskType.parentId === rt.id &&
            lodashFind(subRTs, ["id", riskType.id])
          ) {
            rtArr.push(riskType);
          }
        });
      });

      this.riskTypes = rtArr;

      if (this.givenRating) {
        this.selectedRating = this.givenRating;
      }
    },
    handleChangeRating() {
      this.$emit(EMITTED_ACTIONS.CHANGE, this.selectedRating);
    },
    handleClickDeleteRating(index) {
      this.$emit(EMITTED_ACTIONS.DELETE, index);
    },
    handleClickClearRating() {
      this.$emit(EMITTED_ACTIONS.CLEAR);
    },
  },
  watch: {
    givenRating: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.selectedRating = newVal;
        }
      },
      deep: true,
    },
  },
};
</script>

<style>
.rating-list .v-select__selection {
  font-size: 24px !important;
  line-height: 28px !important;
}
.rating-list .v-text-field__details {
  display: none !important;
}
</style>

<style scoped></style>
